/** Signals memory — what the company has told Signals that its data cannot say.
 *
 * Every fact here changes answers for everyone in the company, so only people
 * with the Signals memory permission (memory_admin) add or remove one
 * directly. Anyone else suggests, and the fact applies once one of them
 * approves it. The server enforces all of that (backend/chainlit/memory_api.py);
 * this page only shows what the caller may do.
 *
 * Socketless (App.tsx SOCKETLESS_ROUTES). Built phone-first.
 */
import { Brain, Check, Clock, Loader2, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { Header } from '@/components/header';
import {
  MemoryState,
  addMemory,
  forgetFact,
  getMemory,
  reviewProposal,
  withdrawProposal
} from '@/components/memory/api';
import { Button } from '@/components/ui/button';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Textarea } from '@/components/ui/textarea';

import { useFeatures } from '@/hooks/useFeatures';

const MAX_FACT = 300;

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

export default function Memory() {
  const { features, loading: flagsLoading } = useFeatures();
  const [state, setState] = useState<MemoryState | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );

  const load = useCallback(
    () =>
      getMemory()
        .then(setState)
        .catch((e) => setNotice({ kind: 'err', text: e.message })),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    setNotice(null);
    try {
      await fn();
      setNotice({ kind: 'ok', text: ok });
      await load();
    } catch (e: any) {
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setBusy(null);
    }
  };

  if (!flagsLoading && !features.tenant_memory) return <Navigate replace to="/" />;

  const admin = !!state?.can_manage;
  const fact = draft.trim();
  const facts = state?.facts ?? [];
  const proposals = state?.proposals ?? [];

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full flex-col">
        <Header />
        <div className="flex-1 overflow-auto">
          <div className="mx-auto max-w-2xl px-4 pb-24 pt-5 sm:px-6 sm:pt-8">
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              <Brain className="size-3.5" /> Signals memory
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              What Signals knows about your company
            </h1>
            <p className="mt-2 text-[15px] text-muted-foreground">
              Facts your data cannot say on its own: a branch that closed, a
              product line you dropped, a store that is really a warehouse. Each
              one shapes the answers everyone in your company gets.
            </p>

            {notice && (
              <div
                className={`mt-4 rounded-lg border px-3 py-2 text-sm ${
                  notice.kind === 'ok'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                    : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200'
                }`}
              >
                {notice.text}
              </div>
            )}

            <section className="mt-5 rounded-2xl border bg-card p-4 sm:p-5">
              <label htmlFor="memory-fact" className="text-sm font-medium">
                Add to Signals memory
              </label>
              <Textarea
                id="memory-fact"
                className="mt-2 min-h-[84px] text-[15px]"
                placeholder="For example: The Heliopolis branch closed in March 2026."
                value={draft}
                maxLength={MAX_FACT + 50}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <span
                  className={`text-xs ${
                    fact.length > MAX_FACT ? 'text-red-600' : 'text-muted-foreground'
                  }`}
                >
                  {admin
                    ? 'It applies to everyone from their next chat.'
                    : 'Your admin approves it before it applies to everyone.'}{' '}
                  {fact.length}/{MAX_FACT}
                </span>
                <Button
                  disabled={!fact || fact.length > MAX_FACT || busy !== null || !state}
                  onClick={() =>
                    run(
                      'add',
                      async () => {
                        const r = await addMemory(fact);
                        setDraft('');
                        return r;
                      },
                      admin
                        ? 'Remembered. It applies from the next chat.'
                        : 'Sent to your admin for approval.'
                    )
                  }
                >
                  {busy === 'add' && <Loader2 className="mr-1 size-4 animate-spin" />}
                  {admin ? 'Remember' : 'Suggest'}
                </Button>
              </div>
            </section>

            {proposals.length > 0 && (
              <section className="mt-6">
                <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
                  <Clock className="size-4 text-muted-foreground" />
                  {admin ? 'Waiting for your approval' : 'Your suggestions waiting for approval'}
                  <span className="text-muted-foreground">· {proposals.length}</span>
                </h2>
                <div className="flex flex-col gap-2">
                  {proposals.map((p) => (
                    <div
                      key={p.id}
                      className="rounded-xl border border-primary/30 bg-primary/5 p-3"
                    >
                      <div className="text-[15px]">{p.fact}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {p.mine ? 'You suggested this' : `Suggested by ${p.by || 'someone'}`}{' '}
                        · {day(p.at)}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {admin && (
                          <>
                            <Button
                              size="sm"
                              disabled={busy !== null}
                              onClick={() =>
                                run(
                                  `ok${p.id}`,
                                  () => reviewProposal(p.id, 'approve'),
                                  'Approved. It applies to everyone from their next chat.'
                                )
                              }
                            >
                              <Check className="mr-1 size-4" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy !== null}
                              onClick={() =>
                                run(`no${p.id}`, () => reviewProposal(p.id, 'reject'), 'Rejected.')
                              }
                            >
                              <X className="mr-1 size-4" /> Reject
                            </Button>
                          </>
                        )}
                        {p.mine && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy !== null}
                            onClick={() =>
                              run(`w${p.id}`, () => withdrawProposal(p.id), 'Suggestion withdrawn.')
                            }
                          >
                            Withdraw
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-6">
              <h2 className="mb-2 text-sm font-semibold">
                Remembered for your company
                <span className="text-muted-foreground"> · {facts.length}</span>
              </h2>
              {state === null ? (
                <div className="grid place-items-center py-10">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : facts.length === 0 ? (
                <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                  Nothing yet. When you tell Signals something your data cannot
                  know, it can remember it here.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {facts.map((f) => (
                    <div
                      key={f.id}
                      className={`flex items-start justify-between gap-3 rounded-xl border bg-card p-3 ${
                        f.active ? '' : 'opacity-60'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-[15px]">{f.fact}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {f.by || 'unknown'} · {day(f.at)}
                        </div>
                        {!f.active && (
                          <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                            Your data no longer agrees with this, so it stopped applying.
                          </div>
                        )}
                      </div>
                      {admin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Forget this"
                          title="Forget this"
                          disabled={busy !== null}
                          onClick={() => {
                            if (!window.confirm('Forget this for everyone in the company?'))
                              return;
                            run(`f${f.id}`, () => forgetFact(f.id), 'Forgotten.');
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
